import { Button, Box, Stack } from '@mui/material';
import { ForwardIcon } from '@icons/ForwardIcon';
import { FC } from 'react';
import { BackIcon } from '@icons/BackIcon';
import LoadingButton from '@mui/lab/LoadingButton';
import { SubmitIcon } from '@icons/fileUpload/SubmitIcon';
import { refineFonts } from '@/Theming/refineFonts';
import { NextButtonValidation } from './InvestigationUI';

const buttonStyles = {
	whiteSpace: 'nowrap',
	...refineFonts.mediumRegular,
};

interface IBeginInvestigationActionButtonsProps {
	handleOnClickBeginning: () => void;
	beginButtonText: string;
}

export const BeginInvestigationActionButtons: FC<IBeginInvestigationActionButtonsProps> = ({
	handleOnClickBeginning,
	beginButtonText,
}) => {
	return (
		<Stack justifyContent={'center'} direction={'row'}>
			<Button
				sx={buttonStyles}
				data-cy={'pqBeginButton'}
				endIcon={<ForwardIcon />}
				variant={'contained'}
				onClick={handleOnClickBeginning}>
				{beginButtonText}
			</Button>
		</Stack>
	);
};

interface OUCFActionButtonsProps {
	handleOnClickPrev: () => void;
	handleNextClick: () => void;
	loading: boolean;
	isNextButtonDisabled: boolean;
	nextButtonText: string;
	prevButtonText: string;
}

export const OUCFActionButtons: FC<OUCFActionButtonsProps> = ({
	handleNextClick,
	handleOnClickPrev,
	isNextButtonDisabled,
	loading,
	nextButtonText,
	prevButtonText,
}) => {
	return (
		<Box
			width={'100%'}
			sx={{ 
				display: 'grid',
				gridTemplateColumns: '1fr 1fr 1fr',
				backgroundColor: 'white' 
			}}>
			<Stack justifyContent={'start'} alignItems={'start'}>
				<Button
					sx={buttonStyles}
					data-cy={'pqPreviousButton'}
					disabled={loading}
					color={'inherit'}
					startIcon={loading ? null : <BackIcon />}
					variant={'text'}
					onClick={handleOnClickPrev}>
					{prevButtonText}
				</Button>
			</Stack>

			<Stack justifyContent={'center'} alignItems={'center'}>
				<LoadingButton
					sx={{
						...buttonStyles,
						minWidth: '100px',
					}}
					data-cy={'pqNextButton'}
					loading={loading}
					disabled={isNextButtonDisabled}
					endIcon={loading ? null : <ForwardIcon disabled={isNextButtonDisabled} />}
					variant={'contained'}
					onClick={handleNextClick}>
					{nextButtonText}
				</LoadingButton>
			</Stack>
		</Box>
	);
};

interface IRegularInvestigationActionButtonsProps {
	handleOnClickPrev: () => void;
	handleNextClick: (isNextButtonDisabled?: boolean) => void;
	loading: boolean;
	nextButtonValidator: () => NextButtonValidation;
	nextButtonText: string;
	prevButtonText: string;
}

export const RegularInvestigationActionButtons: FC<IRegularInvestigationActionButtonsProps> = ({
	handleNextClick,
	handleOnClickPrev,
	nextButtonValidator,
	loading,
	nextButtonText,
	prevButtonText,
}) => {
	const { validateAfterClick, isNextButtonDisabled } = nextButtonValidator();

	return (
		<Box
			width={'100%'}
			sx={{ 
				display: 'grid',
				gridTemplateColumns: '1fr 1fr 1fr',
				backgroundColor: 'white' 
			}}>
			<Stack justifyContent={'start'} alignItems={'start'}>
				<Button
					sx={buttonStyles}
					data-cy={'pqPreviousButton'}
					disabled={loading}
					color={'inherit'}
					startIcon={loading ? null : <BackIcon />}
					variant={'text'}
					onClick={handleOnClickPrev}>
					{prevButtonText}
				</Button>
			</Stack>

			<Stack
				onClick={isNextButtonDisabled ? () => handleNextClick(true) : () => handleNextClick(false)}
				justifyContent={'center'}
				alignItems={'center'}>
				<LoadingButton
					sx={{
						...buttonStyles,
						minWidth: '100px',
					}}
					data-cy={'pqNextButton'}
					loading={loading}
					disabled={isNextButtonDisabled && !validateAfterClick}
					endIcon={loading ? null : <ForwardIcon disabled={isNextButtonDisabled && !validateAfterClick} />}
					variant={'contained'}>
					{nextButtonText}
				</LoadingButton>
			</Stack>
		</Box>
	);
};

interface IUploadAttachmentsActionButtonsProps {
	handleOnClickPrev: () => void;
	loading: boolean;
	setReviewSupportingDocumentsDialogOpen: (open: boolean) => void;
	prevButtonText: string;
	submitButtonText: string;
	submitButtonDisabled: boolean;
}

export const UploadAttachmentsActionButtons: FC<IUploadAttachmentsActionButtonsProps> = ({
	handleOnClickPrev,
	loading,
	prevButtonText,
	submitButtonText,
	setReviewSupportingDocumentsDialogOpen,
	submitButtonDisabled,
}) => {
	return (
		<Box width={'100%'} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
			<Stack justifyContent={'start'} alignItems={'start'}>
				<Button
					sx={buttonStyles}
					data-cy={'pqPreviousButton'}
					disabled={loading}
					color={'primary'}
					startIcon={loading ? null : <BackIcon primaryColor={'#4657F4'} />}
					variant={'text'}
					onClick={handleOnClickPrev}>
					{prevButtonText}
				</Button>
			</Stack>

			<Stack justifyContent={'center'} alignItems={'center'}>
				<LoadingButton
					sx={buttonStyles}
					data-cy={'pqSubmitButton'}
					loading={loading}
					disabled={submitButtonDisabled}
					startIcon={<SubmitIcon primaryColor={submitButtonDisabled ? '#959595' : '#FFFFFF'} />}
					variant={'contained'}
					onClick={() => setReviewSupportingDocumentsDialogOpen(true)}>
					{submitButtonText}
				</LoadingButton>
			</Stack>
		</Box>
	);
};

interface IEmailSentSuccessfullyActionButtonsProps {
	handleOnClickPrev: () => void;
	prevButtonText: string;
}

export const EmailSentSuccessfullyActionButtons: FC<IEmailSentSuccessfullyActionButtonsProps> = ({
	handleOnClickPrev,
	prevButtonText,
}) => {
	return (
		<Box width={'100%'} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
			<Stack justifyContent={'start'} alignItems={'start'}>
				<Button
					sx={buttonStyles}
					data-cy={'pqPreviousButton'}
					color={'primary'}
					startIcon={<BackIcon primaryColor={'#4657F4'} />}
					variant={'text'}
					onClick={handleOnClickPrev}>
					{prevButtonText}
				</Button>
			</Stack>
		</Box>
	);
};
